/**
 * Agenda Query Service — vistas, inbox, colas, ítems (P12 §5 "Views"/"Items")
 *
 * Contrato exacto del backend; este módulo NO inventa endpoints.
 * La autoridad de permisos/alcance es del backend (capabilities + 404 idéntico).
 */

import { apiClient } from '@/services/api-client.ts'
import { unwrap, pickArray, serializeParams } from './http'
import type {
  AgendaItem,
  AgendaQueryParams,
  AgendaQueueName,
  AuditEntry,
  InboxSummary
} from '@/types/agenda'

/** P13-V3 · R0-AGD-09 — metadatos de la lectura acotada de GET /agenda/team. */
export interface TeamAgendaMeta {
  limit: number
  returned: number
  /** true = había más ítems en el rango que el límite de la consulta */
  truncated: boolean
}

export interface TeamAgendaPage {
  items: AgendaItem[]
  /** null cuando el backend no declara `meta` (no se inventa un "completo") */
  meta: TeamAgendaMeta | null
}

async function getItems(path: string, params?: AgendaQueryParams): Promise<AgendaItem[]> {
  const payload = await unwrap<unknown>(
    apiClient.get(path, { params: params ? serializeParams(params) : undefined })
  )
  return pickArray<AgendaItem>(payload, 'items')
}

function parseTeamMeta(raw: unknown): TeamAgendaMeta | null {
  if (!raw || typeof raw !== 'object') return null
  const meta = raw as Record<string, unknown>
  if (typeof meta.truncated !== 'boolean') return null
  return {
    limit: Number(meta.limit),
    returned: Number(meta.returned),
    truncated: meta.truncated
  }
}

export const agendaQueryService = {
  /** GET /agenda/my — agenda propia (ALL7) */
  getMyAgenda(params: AgendaQueryParams): Promise<AgendaItem[]> {
    return getItems('/agenda/my', params)
  },

  /** GET /agenda/assigned-to-me — asignaciones recibidas */
  getAssignedToMe(params: AgendaQueryParams): Promise<AgendaItem[]> {
    return getItems('/agenda/assigned-to-me', params)
  },

  /** GET /agenda/organized-by-me — reuniones/asignaciones organizadas */
  getOrganizedByMe(params: AgendaQueryParams): Promise<AgendaItem[]> {
    return getItems('/agenda/organized-by-me', params)
  },

  /**
   * GET /agenda/team — matriz de coordinación (PRIVATE → BUSY_ONLY).
   *
   * P13-V3 · R0-AGD-09: el backend acota la lectura y responde `{ success, data, meta: { limit,
   * returned, truncated } }`. `meta` es HERMANO de `data`: el unwrap por defecto del interceptor lo
   * descartaría y un prefijo truncado parecería la agenda completa. Se pide con `skipUnwrap` y se lee
   * el sobre entero; un sobre sin `data` es una violación del contrato (error honesto, no lista vacía).
   */
  async getTeamAgenda(params: AgendaQueryParams & Record<string, unknown>): Promise<TeamAgendaPage> {
    const response = await apiClient.get('/agenda/team', {
      params: serializeParams(params),
      skipUnwrap: true
    } as any)
    const envelope = (response as { data?: unknown })?.data as Record<string, unknown> | null | undefined
    if (!envelope || typeof envelope !== 'object' || !Array.isArray(envelope.data)) {
      throw new Error('Respuesta de /agenda/team inválida: sobre sin data (contrato skipUnwrap)')
    }
    return { items: envelope.data as AgendaItem[], meta: parseTeamMeta(envelope.meta) }
  },

  /**
   * GET /agenda/delegated — agenda de dueños delegados (grant-gated).
   * El backend agrupa por dueño: [{ ownerUserId, delegationId, permissions,
   * items }]; aquí se APLANA a AgendaItem[] (los items ya vienen serializados
   * con privacidad aplicada según el grant).
   */
  async getDelegatedAgenda(params: AgendaQueryParams): Promise<AgendaItem[]> {
    const payload = await unwrap<unknown>(
      apiClient.get('/agenda/delegated', { params: serializeParams(params) })
    )
    const groups = pickArray<{ ownerUserId?: number; items?: AgendaItem[] }>(payload, 'items')
    return groups.flatMap((group) => {
      if (Array.isArray((group as { items?: AgendaItem[] }).items)) {
        return ((group as { items: AgendaItem[] }).items).map((item) => ({
          ...item,
          isDelegatedContext: true
        }))
      }
      // Tolerancia: si el backend entregara items planos, se respetan tal cual
      return [group as unknown as AgendaItem]
    })
  },

  /** GET /agenda/users/:userId — self/delegado/coordinación, si no 404 idéntico */
  getUserAgenda(userId: number, params: AgendaQueryParams): Promise<AgendaItem[]> {
    return getItems(`/agenda/users/${userId}`, params)
  },

  /** GET /agenda/inbox — invitaciones de reunión pendientes del usuario */
  async getInbox(): Promise<AgendaItem[]> {
    const payload = await unwrap<unknown>(apiClient.get('/agenda/inbox'))
    return pickArray<AgendaItem>(payload, 'items', 'invitations')
  },

  /** GET /agenda/inbox/summary — contadores para badges */
  getInboxSummary(): Promise<InboxSummary> {
    return unwrap<InboxSummary>(apiClient.get('/agenda/inbox/summary'))
  },

  /** GET /agenda/queues/:queue — colas operativas */
  async getQueue(queue: AgendaQueueName): Promise<AgendaItem[]> {
    const payload = await unwrap<unknown>(apiClient.get(`/agenda/queues/${queue}`))
    return pickArray<AgendaItem>(payload, 'items')
  },

  /** GET /agenda/items/:id — detalle (enmascarado por privacidad si aplica) */
  getItem(id: number): Promise<AgendaItem> {
    return unwrap<AgendaItem>(apiClient.get(`/agenda/items/${id}`))
  },

  /** GET /agenda/items/:id/audit — historial de auditoría del ítem */
  async getItemAudit(id: number): Promise<AuditEntry[]> {
    const payload = await unwrap<unknown>(apiClient.get(`/agenda/items/${id}/audit`))
    return pickArray<AuditEntry>(payload, 'entries', 'audit')
  }
}

export default agendaQueryService
